// PortalBreakout — PeerJS multiplayer session: room codes, heartbeat, reconnect (see CONTRACT.md)
// Uses the global `Peer` constructor from vendor/peerjs.min.js (loaded before main.js).

import { ICE_ENDPOINT, NET_LAGCOMP_MAX_MS } from './constants.js';

const CODE_PREFIX = 'pb-';
const PEER_PREFIX = 'portalbreakout-';   // namespaced broker id; user-facing code stays 'pb-xxxxx'
const CODE_LEN = 5;
const CODE_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz'; // Crockford base32: no i/l/o/u

// WebRTC needs a TURN relay whenever direct/STUN traversal fails (symmetric or
// carrier-grade NAT — common on mobile networks). PeerJS's default config only
// carries its overloaded community TURN, so real-world cross-network joins died
// with "Could not connect". Explicit ICE set: public STUN + Open Relay Project
// free TURN (shared public credentials by design, TCP fallback for strict
// firewalls) + the PeerJS community TURN as one more candidate source.
const ICE_SERVERS = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  { urls: 'stun:stun.cloudflare.com:3478' },
  {
    urls: ['turn:openrelay.metered.ca:80', 'turn:openrelay.metered.ca:443',
           'turn:openrelay.metered.ca:443?transport=tcp'],
    username: 'openrelayproject', credential: 'openrelayproject',
  },
  {
    urls: ['turn:eu-0.turn.peerjs.com:3478', 'turn:us-0.turn.peerjs.com:3478'],
    username: 'peerjs', credential: 'peerjsp',
  },
];
// ?relay=1 forces TURN-only candidates — simulates a symmetric-NAT pair for testing.
const FORCE_RELAY = typeof location !== 'undefined'
  && new URLSearchParams(location.search).has('relay');

// Optional operator-supplied relay, tried before the public defaults (public free
// TURN is best-effort at most; see README "Multiplayer troubleshooting"):
//   localStorage['pb.ice.v1'] = '[{"urls":"turn:turn.example.com:443","username":"u","credential":"c"}]'
const customIce = () => {
  try {
    const arr = JSON.parse(localStorage.getItem('pb.ice.v1') ?? 'null');
    return Array.isArray(arr) ? arr.filter(s => s && s.urls) : [];
  } catch { return []; }
};

// Short-lived TURN credentials fetched from an operator-run endpoint (see
// server/turn/). URL priority: localStorage 'pb.iceurl.v1' (testing) → the
// ICE_ENDPOINT build constant. Failures never block a session — the game just
// falls back to STUN + best-effort public relays.
let remoteIce = { servers: [], until: 0 };
const iceEndpointUrl = () => {
  try { return localStorage.getItem('pb.iceurl.v1') || ICE_ENDPOINT; } catch { return ICE_ENDPOINT; }
};
const refreshRemoteIce = async () => {
  const url = iceEndpointUrl();
  if (!url) { remoteIce = { servers: [], until: 0 }; return; }
  if (remoteIce.until > Date.now()) return;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(3000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const servers = Array.isArray(json?.iceServers) ? json.iceServers.filter((s) => s?.urls) : [];
    // Refresh at 80% of the credential TTL so creds never expire mid-handshake.
    const ttlS = Math.min(Number(json?.ttl) || 600, 6 * 3600);
    remoteIce = { servers, until: Date.now() + ttlS * 800 };
  } catch (err) {
    console.warn('net: ICE endpoint unavailable, using public fallbacks', err);
  }
};

const peerOptions = () => ({
  config: {
    iceServers: [...customIce(), ...remoteIce.servers, ...ICE_SERVERS],
    ...(FORCE_RELAY ? { iceTransportPolicy: 'relay' } : {}),
  },
});

const HB_SEND_MS = 500;   // ping/echo cadence; liveness thresholds below are unchanged
const WATCH_MS = 500;
const LOST_AFTER_MS = 3000;
const GIVE_UP_AFTER_MS = 30000;
const REPAIR_EVERY_MS = 5000;
const SETUP_TIMEOUT_MS = 20000;
const HOST_ID_TRIES = 3;

let peer = null;
let conn = null;
let pendingConn = null;   // guest transport-repair attempt in flight
let pendingSince = 0;
let cb = null;
let role = null;          // 'host' | 'guest' | null
let status = 'idle';      // 'idle' | 'starting' | 'waiting' | 'connected' | 'lost'
let hostPeerId = null;
let lastReceived = 0;
let rtt = 0;              // smoothed round-trip time (ms) from the hb/hback ping-echo; 0 = unknown
let lostAt = 0;
let hbTimer = 0;
let watchTimer = 0;
let repairTimer = 0;
let setupTimer = 0;

const emit = (event) => {
  if (!cb) return;
  try { cb(event); } catch (err) { console.error('net: callback threw', err); }
};

const randomSuffix = () => {
  const bytes = new Uint8Array(CODE_LEN);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let i = 0; i < CODE_LEN; i++) bytes[i] = (Math.random() * 256) | 0;
  let s = '';
  for (const b of bytes) s += CODE_ALPHABET[b % 32]; // 256 % 32 === 0 → unbiased
  return s;
};

const normalizeSuffix = (input) => {
  let s = String(input ?? '').trim().toLowerCase();
  if (s.startsWith(CODE_PREFIX)) s = s.slice(CODE_PREFIX.length);
  // Crockford decode aliases: hand-typed o/i/l map to the digits they resemble
  s = s.replace(/o/g, '0').replace(/[il]/g, '1');
  return /^[0-9a-hj-km-np-tv-z]{5}$/.test(s) ? s : null;
};

const messageFor = (err) => {
  switch (err?.type) {
    case 'peer-unavailable': return 'Room not found — check the code';
    case 'browser-incompatible': return 'This browser does not support WebRTC, which multiplayer needs';
    case 'network': return 'Cannot reach the multiplayer server — check your connection';
    case 'server-error': return 'The multiplayer server had a problem — try again later';
    case 'ssl-unavailable': return 'Multiplayer needs a secure (https) connection';
    case 'unavailable-id': return 'Could not reserve a room code — try again';
    case 'socket-error':
    case 'socket-closed': return 'Lost the connection to the multiplayer server';
    default: return err?.message ? `Connection error: ${err.message}` : 'Connection error';
  }
};

const clearTimers = () => {
  clearInterval(hbTimer);
  clearInterval(watchTimer);
  clearInterval(repairTimer);
  clearTimeout(setupTimer);
  hbTimer = watchTimer = repairTimer = setupTimer = 0;
};

// Full session teardown. Handlers on old peer/conn objects self-filter via
// identity checks, so nulling state before destroy() suppresses reactive events.
const teardown = (notifyClosed) => {
  clearTimers();
  const oldPeer = peer;
  const oldConn = conn;
  const oldPending = pendingConn;
  const listener = cb;
  const wasActive = status !== 'idle';
  peer = null; conn = null; pendingConn = null; pendingSince = 0;
  hostPeerId = null; role = null; cb = null;
  status = 'idle'; rtt = 0;   // never leak a stale RTT into the next session
  try { oldPending?.close(); } catch { /* already dead */ }
  try { oldConn?.close(); } catch { /* already dead */ }
  try { oldPeer?.destroy(); } catch { /* already dead */ }
  if (notifyClosed && wasActive && listener) {
    try { listener({ type: 'closed' }); } catch (err) { console.error('net: callback threw', err); }
  }
};

const stopRepair = () => {
  clearInterval(repairTimer);
  repairTimer = 0;
  if (pendingConn) { try { pendingConn.close(); } catch { /* ignore */ } }
  pendingConn = null;
  pendingSince = 0;
};

const startHeartbeat = () => {
  clearInterval(hbTimer);
  hbTimer = setInterval(() => {
    if (!conn?.open) return;
    try { conn.send({ t: 'hb', ts: performance.now() }); } catch { /* channel died mid-send */ }
  }, HB_SEND_MS);
};

const startWatchdog = () => {
  clearInterval(watchTimer);
  watchTimer = setInterval(() => {
    const now = performance.now();
    if (status === 'connected' && now - lastReceived > LOST_AFTER_MS) {
      markLost();
    } else if (status === 'lost' && now - lostAt > GIVE_UP_AFTER_MS) {
      teardown(true);
    }
  }, WATCH_MS);
};

const markLost = () => {
  if (status !== 'connected') return;
  status = 'lost';
  lostAt = performance.now();
  emit({ type: 'lost' });
  if (role === 'guest') startRepair();
};

// Any incoming message (hb/hback included) counts as liveness. The heartbeat is
// a ping/echo: on 'hb' we bounce the sender's ts straight back as 'hback'; on
// 'hback' we fold (now - ts) into a smoothed RTT (EWMA). Neither the ping nor
// the echo is ever forwarded to the game layer.
const handleMessage = (raw) => {
  lastReceived = performance.now();
  if (status === 'lost') {
    status = 'connected';
    stopRepair();
    emit({ type: 'reconnected' });
  }
  if (raw && typeof raw === 'object') {
    if (raw.t === 'hb') {
      if (conn?.open) { try { conn.send({ t: 'hback', ts: raw.ts }); } catch { /* channel died mid-send */ } }
      return;
    }
    if (raw.t === 'hback') {
      const sample = performance.now() - raw.ts;
      if (Number.isFinite(sample) && sample >= 0) rtt = rtt ? rtt * 0.8 + sample * 0.2 : sample;
      return;
    }
  }
  emit({ type: 'data', msg: raw });
};

const wireConn = (c) => {
  c.on('data', (raw) => {
    if (c !== conn || status === 'idle') return;
    handleMessage(raw);
  });
  c.on('close', () => {
    if (c !== conn || status === 'idle') return;
    markLost();
  });
  c.on('error', () => {
    if (c !== conn || status === 'idle') return;
    markLost();
  });
};

// Make `c` the live channel. Returns the status it replaced so callers can
// pick the right event (peer-joined vs reconnected vs silent replacement).
const adopt = (c) => {
  clearTimeout(setupTimer);
  setupTimer = 0;
  const old = conn;
  conn = c;
  if (pendingConn === c) { pendingConn = null; pendingSince = 0; }
  if (old && old !== c) { try { old.close(); } catch { /* ignore */ } }
  lastReceived = performance.now();
  startHeartbeat();
  startWatchdog();
  const was = status;
  status = 'connected';
  stopRepair();
  return was;
};

// Guest-only: while 'lost' with a closed channel, redial the host every 5 s.
// The host replaces its dead connection with the new one.
const startRepair = () => {
  if (repairTimer) return;
  const tick = () => {
    if (status !== 'lost' || !peer || peer.destroyed) { stopRepair(); return; }
    if (conn?.open) return; // channel alive but silent — wait for data, no redial needed
    const now = performance.now();
    if (pendingConn) {
      if (now - pendingSince < REPAIR_EVERY_MS * 2) return; // attempt still plausible
      try { pendingConn.close(); } catch { /* ignore */ }
      pendingConn = null;
      pendingSince = 0;
    }
    if (peer.disconnected) {
      try { peer.reconnect(); } catch { /* destroyed under us */ }
      return; // need the broker back before we can redial
    }
    let c;
    try { c = peer.connect(hostPeerId, { reliable: true }); } catch { return; }
    pendingConn = c;
    pendingSince = now;
    wireConn(c);
    c.on('open', () => {
      if (c !== pendingConn || status === 'idle') { try { c.close(); } catch { /* ignore */ } return; }
      const was = adopt(c);
      if (was === 'lost') emit({ type: 'reconnected' });
    });
    c.on('close', () => {
      if (c === pendingConn) { pendingConn = null; pendingSince = 0; }
    });
    c.on('error', () => {
      if (c === pendingConn) { pendingConn = null; pendingSince = 0; }
    });
  };
  tick(); // immediate first attempt (covers "channel already closed" case)
  repairTimer = setInterval(tick, REPAIR_EVERY_MS);
};

// Host side: accept the first guest; reject extras while a live channel exists.
// While 'lost' (or the old channel is already closed) a new connection replaces it.
const handleIncomingConnection = (c) => {
  if (conn?.open && status !== 'lost') {
    try { c.close(); } catch { /* ignore */ }
    c.on('open', () => { try { c.close(); } catch { /* ignore */ } });
    return;
  }
  wireConn(c);
  c.on('open', () => {
    if (!peer || status === 'idle') { try { c.close(); } catch { /* ignore */ } return; }
    const was = adopt(c);
    if (was === 'waiting') emit({ type: 'peer-joined' });
    else if (was === 'lost') emit({ type: 'reconnected' });
    // was 'connected': silent replacement of a channel that died before the watchdog noticed
  });
};

const host = async (listener) => {
  teardown(false); // silently drop any previous session
  cb = listener;
  if (typeof Peer === 'undefined') {
    throw new Error('Multiplayer requires the PeerJS library (are you offline?)');
  }
  role = 'host';
  status = 'starting';
  await refreshRemoteIce();
  return new Promise((resolve, reject) => {
    let settled = false;
    const fail = (message) => {
      if (settled) return;
      settled = true;
      // no 'error' emit here: the rejection is the single failure channel
      // (emitting too made every failure toast twice in main.js)
      teardown(false);
      reject(new Error(message));
    };
    let tries = 0;
    const attempt = () => {
      tries += 1;
      const suffix = randomSuffix();
      const roomCode = CODE_PREFIX + suffix;
      let p;
      try { p = new Peer(PEER_PREFIX + suffix, peerOptions()); } catch { fail('Could not start multiplayer — try again'); return; }
      peer = p;
      clearTimeout(setupTimer);
      setupTimer = setTimeout(() => {
        if (p === peer && !settled) fail('Could not reach the multiplayer server — try again');
      }, SETUP_TIMEOUT_MS);
      p.on('open', () => {
        if (p !== peer) return;
        clearTimeout(setupTimer);
        setupTimer = 0;
        status = 'waiting';
        if (!settled) { settled = true; resolve(roomCode); }
      });
      p.on('disconnected', () => {
        // dropped from the signaling broker; rejoin so the guest can still (re)dial us
        if (p === peer && !p.destroyed) { try { p.reconnect(); } catch { /* ignore */ } }
      });
      p.on('connection', (c) => {
        if (p !== peer) return;
        handleIncomingConnection(c);
      });
      p.on('error', (err) => {
        if (p !== peer) return;
        const type = err?.type;
        if (type === 'unavailable-id' && !settled) {
          try { p.destroy(); } catch { /* ignore */ }
          if (tries < HOST_ID_TRIES) attempt();
          else fail('Could not reserve a room code — try again');
          return;
        }
        if (type === 'network' || type === 'peer-unavailable') return; // transient broker blip / redial noise
        if (!settled) { fail(messageFor(err)); return; }
        emit({ type: 'error', message: messageFor(err) });
        // fatal error with no live channel → the room is dead, end the session
        if (p.destroyed && !conn?.open) teardown(true);
      });
    };
    attempt();
  });
};

const join = async (roomCode, listener) => {
  teardown(false);
  cb = listener;
  if (typeof Peer === 'undefined') {
    throw new Error('Multiplayer requires the PeerJS library (are you offline?)');
  }
  const suffix = normalizeSuffix(roomCode);
  if (!suffix) {
    throw new Error('Invalid room code — expected pb- plus 5 letters/numbers');
  }
  role = 'guest';
  status = 'starting';
  hostPeerId = PEER_PREFIX + suffix;
  await refreshRemoteIce();
  return new Promise((resolve, reject) => {
    let settled = false;
    const fail = (message) => {
      if (settled) return;
      settled = true;
      // no 'error' emit here: the rejection is the single failure channel
      teardown(false);
      reject(new Error(message));
    };
    let p;
    try { p = new Peer(peerOptions()); } catch { fail('Could not start multiplayer — try again'); return; }
    peer = p;
    setupTimer = setTimeout(() => {
      if (p === peer && !settled) fail('Connection timed out — a VPN or strict network may block peer-to-peer. Try again');
    }, SETUP_TIMEOUT_MS);
    p.on('disconnected', () => {
      if (p === peer && !p.destroyed) { try { p.reconnect(); } catch { /* ignore */ } }
    });
    p.on('error', (err) => {
      if (p !== peer) return;
      if (!settled) { fail(messageFor(err)); return; }
      const type = err?.type;
      if (type === 'network' || type === 'peer-unavailable') return; // repair redials handle these
      emit({ type: 'error', message: messageFor(err) });
    });
    p.on('open', () => {
      if (p !== peer) return;
      let c;
      try { c = p.connect(hostPeerId, { reliable: true }); } catch { fail('Could not connect — try again'); return; }
      wireConn(c);
      c.on('open', () => {
        if (p !== peer || status === 'idle') { try { c.close(); } catch { /* ignore */ } return; }
        adopt(c);
        if (!settled) { settled = true; resolve(); }
        emit({ type: 'open' });
      });
      c.on('close', () => { if (!settled) fail('Connection failed — try again'); });
      c.on('error', (err) => {
        console.warn('net: guest connection error', err);
        if (!settled) fail('Could not reach your friend — a VPN or strict network may block peer-to-peer. Try again');
      });
    });
  });
};

const send = (msg) => {
  if (!conn?.open) return;
  try { conn.send(msg); } catch { /* channel torn down mid-send — watchdog will notice */ }
};

const close = () => teardown(true);

export const net = {
  host,
  join,
  send,
  close,
  get connected() { return status === 'connected' && conn?.open === true; },
  get role() { return role; },
  get rtt() { return rtt; },                                            // smoothed RTT ms, 0 if unknown
  get oneWay() { return Math.min(NET_LAGCOMP_MAX_MS, Math.max(0, rtt / 2)); }, // clamped half-RTT ms
};
